import {
  CustomMappingType,
  ITranslationMap,
  type ICustomMapping,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import { ServiceProviderHandler } from '@principle-theorem/principle-core';
import {
  ITreatmentConfiguration,
  type IServiceCode,
} from '@principle-theorem/principle-core/interfaces';
import {
  WithPrimaryKey,
  isINamedDocument,
  type INamedDocument,
  type WithRef,
} from '@principle-theorem/shared';
import { sortBy } from 'lodash';
import { CustomMapping } from '../../../custom-mapping';
import { IBaseMigrationItemCode } from '../../../interfaces';
import { ItemCodesMappingHandler } from '../../../mappings/item-codes';
import {
  ITEM_CODE_CUSTOM_MAPPING_TYPE,
  ItemCodeResourceMapType,
} from '../../../mappings/item-codes-to-xlsx';
import { IPraktikaAppointmentProcedure } from '../../source/entities/appointment-procedure';
import {
  FeeScheduleSourceEntity,
  IPraktikaFeeSchedule,
  PraktikaFeeSchedule,
} from '../../source/entities/fee-schedule';

export const ITEM_CODE_MAPPING: ICustomMapping = CustomMapping.init({
  metadata: {
    label: 'Item Codes',
    description: `Used for mapping notes on non-ada item codes to Principle.

    As Praktika allows for the creation of non ADA item codes, we need to allow the mapping of non-ADA codes that were used for treatment, to be mapped to an official ADA code/Treatment Configuration.

    In the case that a treatment has a fee, the treatment will be reflected on the invoice as expected, regardless of the item code.`,
    type: ITEM_CODE_CUSTOM_MAPPING_TYPE,
  },
  type: CustomMappingType.SelectionList,
});

export class PraktikaItemCodeMappingHandler extends ItemCodesMappingHandler<
  PraktikaFeeSchedule,
  FeeScheduleSourceEntity
> {
  override sourceEntity = new FeeScheduleSourceEntity();
  override translateFn = (
    _record: IPraktikaFeeSchedule
  ): IBaseMigrationItemCode => {
    return {} as IBaseMigrationItemCode;
  };

  override async getItemCodeOptions(
    migration: WithRef<IPracticeMigration>
  ): Promise<IBaseMigrationItemCode[]> {
    const feeScheduleSource = new FeeScheduleSourceEntity();
    const records = await feeScheduleSource
      .getAllRecords$(migration)
      .toPromise();
    const itemCodes = records
      .map((record) => {
        return record.data.items;
      })
      .flat()
      .map((item) => ({
        id: item.custom_code || item.code,
        itemCode: item.custom_code || item.code,
        description: item.custom_description || '',
      }));
    return sortBy(
      itemCodes.filter(
        (record) => !ServiceProviderHandler.findServiceCode(record.itemCode)
      ),
      'itemCode'
    );
  }
}

export function resolveMappedCode(
  sourceItemCodes: WithPrimaryKey<
    ITranslationMap<object, ItemCodeResourceMapType>
  >[],
  sourceTreatment: IPraktikaAppointmentProcedure
):
  | IServiceCode
  | INamedDocument<ITreatmentConfiguration>
  | ItemCodeResourceMapType.Omit
  | undefined {
  const procedureCode =
    sourceTreatment.code.toString() || sourceTreatment.ada_code?.toString();

  const mappedCode = sourceItemCodes.find(
    (sourceItemCode) => sourceItemCode.sourceIdentifier === procedureCode
  );

  if (mappedCode?.destinationValue?.value === ItemCodeResourceMapType.Omit) {
    return ItemCodeResourceMapType.Omit;
  }

  if (
    mappedCode?.destinationValue?.value ===
      ItemCodeResourceMapType.TreatmentConfiguration &&
    mappedCode?.associatedValue &&
    isINamedDocument<ITreatmentConfiguration>(mappedCode.associatedValue)
  ) {
    return mappedCode.associatedValue;
  }

  let code = ServiceProviderHandler.findServiceCode(sourceTreatment.code);
  if (!code && sourceTreatment.ada_code) {
    code = ServiceProviderHandler.findServiceCode(sourceTreatment.ada_code);
  }

  if (code) {
    return code;
  }

  if (
    mappedCode?.destinationValue?.value !==
      ItemCodeResourceMapType.ServiceCode ||
    !mappedCode?.associatedValue
  ) {
    if (procedureCode && PRAKTIKA_SPECIAL_CODES[procedureCode]) {
      return ItemCodeResourceMapType.Omit;
    }

    // throw new Error(`Could not find service code for ${procedureCode}`);
    return;
  }

  if (isINamedDocument(mappedCode.associatedValue)) {
    return ServiceProviderHandler.findServiceCode(
      mappedCode.associatedValue.ref.id
    );
  }

  return ServiceProviderHandler.findServiceCode(mappedCode.associatedValue);
}

export const PRAKTIKA_SPECIAL_CODES: { [key: string]: string } = {
  LB: 'Loyalty Bonus',
  '000': 'Free Service',
};
